import React, { useState, useEffect } from 'react'
import { Box, Typography, TextareaAutosize, Alert } from '@mui/material'
import { Field, useField } from 'react-final-form'
import ImageUploader from '../components/ImageUploader'
import ImageViewer from '../components/ImageViewer'
import { useParams } from 'react-router-dom'
import {
  DEFAULT_DARK_TITLE_TEXT_COLOR,
  DEFAULT_DARK_MESSAGE_TEXT_COLOR,
  DEFAULT_LIGHT_THEME_PLACEHOLDER_COLOR
} from '../../../../common/constants/themeConstants'

const MAX_PREVIEW_IMAGE_WIDTH = 460
const MAX_PREVIEW_IMAGE_HEIGHT = 315

export default function TextImageModule({ name, isPreview = false, data, isCurrentThemeLight }) {
  const { topicId } = useParams()
  const [titleError, setTitleError] = useState(null)
  const [messageError, setMessageError] = useState(null)
  const [previewImageStyle, setPreviewImageStyle] = useState({ opacity: 0 })

  const MAX_TITLE_LENGTH = 400
  const MAX_MESSAGE_LENGTH = 1000

  const textColor = isCurrentThemeLight ? DEFAULT_DARK_TITLE_TEXT_COLOR : 'rgba(255, 255, 255, 1)'
  const descriptionColor = isCurrentThemeLight ? DEFAULT_DARK_MESSAGE_TEXT_COLOR : 'rgba(255, 255, 255, 1)'
  const placeholderColor = isCurrentThemeLight ? DEFAULT_LIGHT_THEME_PLACEHOLDER_COLOR : 'rgba(255, 255, 255, 0.3)'

  useEffect(() => {
    if (!isPreview) {
      setPreviewImageStyle({ opacity: 0 })
      return
    }

    if (!data || !data.imageUrl) {
      setPreviewImageStyle({
        width: `${MAX_PREVIEW_IMAGE_WIDTH}px`,
        height: `${MAX_PREVIEW_IMAGE_HEIGHT}px`,
        opacity: 0
      })
      return
    }

    setPreviewImageStyle(s => ({
      width: s.width || `${MAX_PREVIEW_IMAGE_WIDTH}px`,
      height: s.height || `${MAX_PREVIEW_IMAGE_HEIGHT}px`,
      opacity: 0,
      transition: 'opacity 0.15s ease-out'
    }))

    const img = new Image()
    img.onload = () => {
      const naturalWidth = img.naturalWidth
      const naturalHeight = img.naturalHeight

      let displayHeight = MAX_PREVIEW_IMAGE_HEIGHT
      let displayWidth = (naturalWidth / naturalHeight) * displayHeight

      if (displayWidth > MAX_PREVIEW_IMAGE_WIDTH) {
        displayWidth = MAX_PREVIEW_IMAGE_WIDTH
      }

      setPreviewImageStyle({
        width: `${displayWidth}px`,
        height: `${displayHeight}px`,
        opacity: 1,
        transition: 'opacity 0.3s ease-in 0.1s'
      })
    }
    img.onerror = () => {
      setPreviewImageStyle({
        width: `${MAX_PREVIEW_IMAGE_WIDTH}px`,
        height: `${MAX_PREVIEW_IMAGE_HEIGHT}px`,
        opacity: 1,
        transition: 'opacity 0.3s ease-in 0.1s'
      })
    }
    img.src = data.imageUrl
  }, [isPreview, data])

  const handleTitleChange = (input, value) => {
    if (value.length > MAX_TITLE_LENGTH) {
      setTitleError(`Title cannot exceed ${MAX_TITLE_LENGTH} characters`)
      return
    }

    setTitleError(null)
    input.onChange(value)
  }

  const handleMessageChange = (input, value) => {
    if (value.length > MAX_MESSAGE_LENGTH) {
      setMessageError(`Message cannot exceed ${MAX_MESSAGE_LENGTH} characters`)
      return
    }

    setMessageError(null)
    input.onChange(value)
  }

  if (isPreview && data) {
    const alignment = data.alignment || 'LEFT'
    return (
      <Box sx={styles.previewContainer}>
        {data.imageUrl && (
          <Box
            sx={{
              ...styles.previewImageContainer,
              ...previewImageStyle,
              alignSelf: alignment === 'CENTER' ? 'center' : alignment === 'RIGHT' ? 'flex-end' : 'flex-start'
            }}
          >
            <ImageViewer imageUrl={data.imageUrl} alt={data.title || 'Module image'} alignment={alignment} />
          </Box>
        )}

        <Box sx={styles.previewTextContainer}>
          <Typography
            sx={{
              ...styles.previewTitle,
              color: data.title ? textColor : placeholderColor,
              textAlign: alignment,
              whiteSpace: 'pre-wrap'
            }}
          >
            {data.title || 'No title'}
          </Typography>

          <Typography
            sx={{
              ...styles.previewMessage,
              color: data.message ? descriptionColor : placeholderColor,
              textAlign: alignment,
              whiteSpace: 'pre-wrap'
            }}
          >
            {data.message || 'No content'}
          </Typography>
        </Box>
      </Box>
    )
  }

  const alignmentField = useField(`${name}.alignment`)
  const alignment = alignmentField.input.value || 'LEFT'

  return (
    <Box sx={styles.container}>
      <Field name={`${name}.imageUrl`}>
        {({ input }) => (
          <Box
            sx={{
              alignSelf: alignment === 'CENTER' ? 'center' : alignment === 'RIGHT' ? 'flex-end' : 'flex-start'
            }}
          >
            <ImageUploader
              topicId={topicId}
              imageUrl={input.value}
              onImageChange={input.onChange}
              onImageRemove={() => input.onChange('')}
              alignment={alignment}
            />
          </Box>
        )}
      </Field>

      {titleError && (
        <Alert severity='error' sx={styles.alert}>
          {titleError}
        </Alert>
      )}
      <Field name={`${name}.title`}>
        {({ input }) => (
          <Box sx={styles.fieldContainer}>
            <TextareaAutosize
              {...input}
              placeholder='Enter a card title or main message'
              style={{
                ...styles.titleInput,
                color: textColor,
                textAlign: alignment
              }}
              onChange={e => handleTitleChange(input, e.target.value)}
              maxLength={MAX_TITLE_LENGTH}
            />
          </Box>
        )}
      </Field>

      {messageError && (
        <Alert severity='error' sx={styles.alert}>
          {messageError}
        </Alert>
      )}
      <Field name={`${name}.message`}>
        {({ input }) => (
          <Box sx={styles.fieldContainer}>
            <TextareaAutosize
              {...input}
              placeholder='Enter text using short, simple sentences. You can also add lists and bullet points.'
              style={{
                ...styles.messageInput,
                color: descriptionColor,
                textAlign: alignment
              }}
              onChange={e => handleMessageChange(input, e.target.value)}
              maxLength={MAX_MESSAGE_LENGTH}
            />
          </Box>
        )}
      </Field>
    </Box>
  )
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    height: '100%',
    overflow: 'auto',
    scrollBehavior: 'smooth',
    '&::-webkit-scrollbar': {
      width: '6px'
    },
    '&::-webkit-scrollbar-track': {
      background: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '10px'
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: '10px'
    },
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(97, 35, 255, 0.5) rgba(255, 255, 255, 0.1)'
  },
  previewContainer: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
    gap: '16px',
    padding: '16px'
  },
  previewImageContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    maxWidth: `${MAX_PREVIEW_IMAGE_WIDTH}px`,
    overflow: 'hidden',
    borderRadius: '8px',
    border: '1px solid rgba(0, 0, 0, 0.12)',
    boxSizing: 'border-box',
    position: 'relative',
    transition: 'width 0.3s ease, height 0.3s ease, opacity 0.3s ease-in-out',
    '&:hover': {
      boxShadow: '0px 0px 10px rgba(97, 35, 255, 0.5)'
    }
  },
  previewTextContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  previewTitle: {
    fontFamily: 'DM Sans',
    fontSize: '42px',
    fontWeight: 500,
    color: 'rgba(255, 255, 255, 1)',
    wordBreak: 'break-word'
  },
  previewMessage: {
    fontFamily: 'Satoshi',
    fontSize: '24px',
    fontWeight: 500,
    color: 'rgba(255, 255, 255, 1)',
    wordBreak: 'break-word'
  },
  fieldContainer: {
    position: 'relative',
    width: '100%'
  },
  title: {
    fontFamily: 'Satoshi',
    fontSize: '42px',
    fontWeight: 900,
    cursor: 'text',
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap'
  },
  message: {
    fontFamily: 'Satoshi',
    fontSize: '26px',
    fontWeight: 500,
    lineHeight: '1.8',
    cursor: 'text',
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap'
  },
  titleInput: {
    width: '100%',
    fontSize: '42px',
    fontWeight: 500,
    border: 'none',
    outline: 'none',
    resize: 'none',
    fontFamily: 'DM Sans',
    padding: 0,
    margin: 0,
    backgroundColor: 'transparent',
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap'
  },
  messageInput: {
    width: '100%',
    fontSize: '24px',
    fontWeight: 500,
    border: 'none',
    outline: 'none',
    resize: 'none',
    fontFamily: 'Satoshi',
    lineHeight: '1.8',
    padding: 0,
    margin: 0,
    backgroundColor: 'transparent',
    flex: 1,
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap'
  },
  alert: {
    width: '100%',
    marginBottom: '8px'
  }
}
